import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "./api";

/** Background updates retain rows and never overwrite an editor's local state. */
export function useLiveResource<T>(path: string, live = false) {
  const [snapshot, setSnapshot] = useState<{ path: string; data: T; at: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [failure, setFailure] = useState<unknown>(null);
  const request = useRef(0);
  const pending = useRef<AbortController | null>(null);
  const load = useCallback(async (quiet = false) => {
    if (quiet && (pending.current || document.hidden)) return;
    pending.current?.abort();
    const controller = new AbortController();
    pending.current = controller;
    const id = ++request.current;
    if (!quiet) setLoading(true);
    const timeout = window.setTimeout(() => controller.abort(), 20_000);
    try {
      const data = await api<T>(path, { signal: controller.signal, cache: "no-store" });
      if (id === request.current) {
        setSnapshot({ path, data, at: Date.now() }); setError(""); setFailure(null);
      }
    } catch (reason) {
      if (id === request.current) {
        setError(controller.signal.aborted ? "Сервер отвечает дольше обычного. Повторяем подключение." : reason instanceof Error ? reason.message : String(reason));
        setFailure(reason);
      }
    } finally {
      window.clearTimeout(timeout);
      if (id === request.current) { pending.current = null; setLoading(false); }
    }
  }, [path]);
  useEffect(() => {
    setError(""); setFailure(null); void load();
    const resume = () => { if (!document.hidden) void load(true); };
    const timer = live ? window.setInterval(() => void load(true), 15_000) : undefined;
    if (live) { document.addEventListener("visibilitychange", resume); window.addEventListener("online", resume); }
    return () => {
      // This ref is a request generation counter, not a DOM ref. Invalidate its
      // latest value so every outstanding request is ignored after cleanup.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      request.current++; pending.current?.abort(); pending.current = null;
      window.clearInterval(timer); document.removeEventListener("visibilitychange", resume); window.removeEventListener("online", resume);
    };
  }, [load, live]);
  // Expose a parameterless callback so a button event cannot become `quiet`.
  const refresh = useCallback(() => load(), [load]);
  return { data: snapshot?.path === path ? snapshot.data : null, loading, error, failure, load: refresh, updatedAt: snapshot?.path === path ? snapshot.at : null };
}
